import ApplicationController from 'aura/application-controller';
import { getState, subscribe } from 'aura/variant-state';
import { resolveMedia } from 'aura/media-resolver';
import { replaceImage, cancelImage } from 'aura/image-transition';
export default class GalleryController extends ApplicationController {
  start() {
    this.image = this.element.querySelector('[data-gallery-image]');
    this.view =
      this.element.querySelector('[data-gallery-view][aria-pressed="true"]')?.dataset.galleryView ||
      'campaign';
    this.group = this.element.closest('[data-product-group]')?.dataset.productGroup;
    this.listen(this.element, 'click', (event) => {
      const view = event.target.closest('[data-gallery-view]');
      if (view) {
        event.preventDefault();
        this.view = view.dataset.galleryView;
        this.render(getState(this.group));
      }
      if (event.target.closest('[data-gallery-zoom], [data-gallery-open]')) {
        event.preventDefault();
        if (Date.now() >= (this.suppressOpenUntil || 0))
          this.zoom(event.target.closest('[data-gallery-zoom], [data-gallery-open]'));
      }
      const paging = event.target.closest('[data-gallery-prev], [data-gallery-next]');
      if (paging) {
        event.preventDefault();
        this.page(paging.hasAttribute('data-gallery-next') ? 1 : -1);
      }
    });
    const frame = this.element.querySelector('[data-gallery-frame]');
    this.listen(frame, 'touchstart', (event) => {
      this.swipe = event.touches.length === 1 && (globalThis.visualViewport?.scale ?? 1) <= 1.01 && !event.target.closest('button:not([data-gallery-open]), a')
        ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true });
    this.listen(frame, 'touchmove', (event) => {
      if (event.touches.length !== 1) this.swipe = null;
    }, { passive: true });
    this.listen(frame, 'touchcancel', () => { this.swipe = null; }, { passive: true });
    this.listen(frame, 'touchend', (event) => {
      const start = this.swipe;
      this.swipe = null;
      if (!start || event.touches.length || !event.changedTouches.length || (globalThis.visualViewport?.scale ?? 1) > 1.01) return;
      const dx = event.changedTouches[0].clientX - start.x;
      const dy = event.changedTouches[0].clientY - start.y;
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      this.suppressOpenUntil = Date.now() + 400;
      this.element.querySelector(dx < 0 ? '[data-gallery-next]' : '[data-gallery-prev]')?.click();
    }, { passive: true });
    this.cleanup(subscribe(this.group, (state) => this.render(state)));
    this.render(getState(this.group));
  }
  page(step) {
    const views = [...this.element.querySelectorAll('[data-gallery-view]')].filter(
      (button) => !button.disabled,
    );
    if (!views.length) return;
    const index = views.findIndex((button) => button.dataset.galleryView === this.view);
    views[(index + step + views.length) % views.length]?.click();
  }
  async render(state) {
    if (!state || !this.image) return;
    const variant = state.variant,
      view = this.view;
    const media = resolveMedia(variant, view);
    const ticket = (this.ticket = (this.ticket || 0) + 1);
    this.element.querySelectorAll('[data-gallery-view]').forEach((button) => {
      button.disabled = !resolveMedia(variant, button.dataset.galleryView);
    });
    if (!media) {
      cancelImage(this.image);
      this.unavailable(variant);
      return;
    }
    const changed = await replaceImage(this.image, media);
    if (ticket !== this.ticket || !this.connected) return;
    if (!changed) {
      if (this.viewer && this.selectedMedia && this.displayedVariantId === variant.id) {
        this.view = this.image.parentElement.dataset.view;
        return;
      }
      this.unavailable(variant);
      return;
    }
    this.selectedMedia = media;
    this.displayedVariantId = variant.id;
    this.image.hidden = false;
    this.image.parentElement.dataset.view = view;
    this.element.querySelectorAll('[data-gallery-zoom], [data-gallery-open]').forEach((button) => {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    });
    const buttons = [...this.element.querySelectorAll('[data-gallery-view]')];
    buttons.forEach((button) =>
      button.setAttribute('aria-pressed', String(button.dataset.galleryView === view)),
    );
    const caption = this.element.querySelector('[data-gallery-caption]');
    if (caption)
      caption.textContent = `${variant.title} / ${buttons.find((button) => button.dataset.galleryView === view)?.textContent.trim() || view}`;
    const count = this.element.querySelector('[data-gallery-count]');
    if (count)
      count.textContent = `${buttons.findIndex((button) => button.dataset.galleryView === view) + 1} / ${buttons.length}`;
    if (this.viewer) this.updateZoom();
  }
  unavailable(variant) {
    cancelImage(this.image);
    this.image.hidden = true;
    this.image.parentElement?.setAttribute('aria-busy', 'false');
    this.image.parentElement?.classList.remove('is-loading');
    this.element.querySelectorAll('[data-gallery-zoom], [data-gallery-open]').forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    });
    const caption = this.element.querySelector('[data-gallery-caption]');
    if (caption) caption.textContent = `${variant.title} / Image unavailable`;
    this.closeViewer();
  }
  updateZoom() {
    if (!this.viewer) return;
    const variant = getState(this.group)?.variant;
    if (variant?.id !== this.viewerVariantId) {
      this.closeViewer();
      return;
    }
    const index = this.viewer.options.dataSource.findIndex(item => item.view === this.view);
    if (index >= 0 && index !== this.viewer.currIndex) this.viewer.goTo(index);
  }
  async zoom(opener) {
    if (!this.image || this.image.hidden || this.viewer || this.zoomPending) return;
    this.zoomPending = true;
    const ticket = this.zoomTicket = (this.zoomTicket || 0) + 1;
    try {
      const { openProductLightbox, galleryItems } = await import('aura/product-lightbox');
      if (!this.connected || ticket !== this.zoomTicket || this.image.hidden) return;
      const variant = getState(this.group)?.variant;
      const slides = galleryItems(variant, [...this.element.querySelectorAll('[data-gallery-view]')]);
      if (!slides.length) return;
      this.viewerClosing = false;
      this.viewerVariantId = variant.id;
      this.viewer = openProductLightbox({
        slides,
        index: Math.max(0, slides.findIndex(item => item.view === this.view)),
        opener,
        onChange: view => {
          if (!this.connected || this.viewerClosing) return;
          this.view = view;
          this.render(getState(this.group));
        },
        onClose: () => {
          this.viewer = null;
          this.emit('overlay-change', { open: false });
          if (this.connected && opener?.isConnected) opener.focus({ preventScroll: true });
        },
      });
      this.emit('overlay-change', { open: true });
    } catch {
      const caption = this.element.querySelector('[data-gallery-caption]');
      if (this.connected && caption) caption.textContent = 'The gallery couldn’t load. Please refresh the page and try again.';
    } finally {
      this.zoomPending = false;
    }
  }
  closeViewer(immediate = false) {
    const viewer = this.viewer;
    if (!viewer || this.viewerClosing) return;
    this.viewerClosing = true;
    const close = () => immediate ? viewer.destroy() : viewer.close();
    if (viewer.opener?.isOpening) viewer.on('openingAnimationEnd', close);
    else close();
  }
  stop() {
    this.ticket++;
    this.zoomTicket = (this.zoomTicket || 0) + 1;
    this.closeViewer(true);
    cancelImage(this.image);
  }
}
